<template>
  <div class="wizard">
    <div v-if="step === 1" class="wizard-step">
      <div class="section-header">
        <h3 class="section-title">Insert Tables</h3>
        <p class="section-desc">
          Pick a repeating region below, choose which columns to include, then Insert.
        </p>
      </div>

      <input
        id="wizard-region-search"
        v-model.trim="regionSearch"
        type="search"
        class="search-input"
        placeholder="Search table regions"
        aria-label="Search table regions"
        autocomplete="off"
      >

      <p v-if="props.tags.groups.length === 0" class="empty-hint">
        This source type has no table regions yet.
      </p>
      <p v-else-if="filteredGroups.length === 0" class="empty-hint">
        No regions match &ldquo;{{ regionSearch }}&rdquo;.
      </p>
      <div v-else class="option-list" role="radiogroup" aria-label="Table regions">
        <label
          v-for="group in filteredGroups"
          :key="group.name"
          class="option-item"
        >
          <input
            v-model="selectedGroupName"
            type="radio"
            :value="group.name"
          >
          <span class="option-label">
            <span class="option-title">{{ labelForRegion(group.name) }}</span>
            <span class="option-sub">&#123;&#123;TableStart:{{ group.name }}&#125;&#125;</span>
          </span>
        </label>
      </div>
    </div>

    <div v-else-if="step === 2 && selectedGroup" class="wizard-step">
      <div class="wizard-header">
        <button type="button" class="link-btn" @click="onBack">
          <span aria-hidden="true">&larr;</span> Back
        </button>
        <h3 class="section-title">Choose columns</h3>
      </div>

      <div class="wizard-subheader">
        <span class="wizard-subheader-eyebrow">Region</span>
        <span class="wizard-subheader-title">{{ labelForRegion(selectedGroup!.name) }}</span>
      </div>

      <div class="column-toolbar">
        <span class="column-toolbar-label">Columns ({{ checkedColumns.length }} of {{ selectedGroup!.columns.length }})</span>
        <span class="column-toolbar-actions">
          <button type="button" class="link-btn" @click="selectAllColumns">Select all</button>
          <span class="link-sep">|</span>
          <button type="button" class="link-btn" @click="clearColumns">Clear</button>
        </span>
      </div>

      <div class="option-list">
        <label
          v-for="col in selectedGroup!.columns"
          :key="col"
          class="option-item"
        >
          <input
            v-model="checkedColumns"
            type="checkbox"
            :value="col"
          >
          <span class="option-label">
            <span class="option-title">{{ labelForColumn(col, selectedGroup!.name) }}</span>
            <span class="option-sub">&#123;&#123;{{ col }}&#125;&#125;</span>
          </span>
        </label>
      </div>
    </div>

    <div class="wizard-actions">
      <button
        v-if="step === 1"
        type="button"
        class="btn btn-primary"
        :disabled="!selectedGroupName"
        @click="onNext"
      >
        Next
      </button>
      <button
        v-else
        type="button"
        class="btn btn-primary"
        :disabled="checkedColumns.length === 0 || isInserting"
        @click="onInsert"
      >
        {{ isInserting ? 'Inserting…' : 'Insert' }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { SupportedTags, SupportedTagGroup } from '@/types/SupportedTags';
import { toLabel } from '@/services/tagLabels';
import { insertTable } from '@/services/wordInsertion';

const props = defineProps<{
  tags: SupportedTags;
}>();

const emit = defineEmits<{
  (e: 'inserted'): void;
  (e: 'status', message: string): void;
}>();

const step = ref<1 | 2>(1);
const regionSearch = ref('');
const selectedGroupName = ref<string | null>(null);
const checkedColumns = ref<string[]>([]);
const isInserting = ref(false);

function labelForRegion(name: string): string {
  return props.tags.groupLabels?.[name] ?? toLabel(name);
}

function labelForColumn(col: string, groupName: string): string {
  return props.tags.groupColumnLabels?.[groupName]?.[col] ?? toLabel(col);
}

const filteredGroups = computed<SupportedTagGroup[]>(() => {
  if (regionSearch.value === '') return props.tags.groups;
  const q = regionSearch.value.toLowerCase();
  return props.tags.groups.filter((g: SupportedTagGroup) =>
    g.name.toLowerCase().includes(q) || labelForRegion(g.name).toLowerCase().includes(q),
  );
});

const selectedGroup = computed<SupportedTagGroup | undefined>(() =>
  props.tags.groups.find((g: SupportedTagGroup) => g.name === selectedGroupName.value) ?? undefined,
);

watch(selectedGroup, (group) => {
  checkedColumns.value = group ? [...group.columns] : [];
});

// Source-type switch reloads tags — reset the wizard so a stale group name
// can't be sent to insertTable and Step 2 never renders against an unknown
// region.
watch(
  () => props.tags,
  () => resetWizard(),
);

function resetWizard(): void {
  step.value = 1;
  selectedGroupName.value = null;
  checkedColumns.value = [];
  regionSearch.value = '';
}

function onNext(): void {
  if (selectedGroupName.value) step.value = 2;
}

function onBack(): void {
  if (step.value === 2) step.value = 1;
}

function selectAllColumns(): void {
  const group = selectedGroup.value;
  if (group) checkedColumns.value = [...group.columns];
}

function clearColumns(): void {
  checkedColumns.value = [];
}

async function onInsert(): Promise<void> {
  if (!selectedGroup.value || checkedColumns.value.length === 0) return;
  const group = selectedGroup.value;
  // Preserve the backend column order regardless of check toggle sequence.
  const columnsInOrder = group.columns.filter((c: string) => checkedColumns.value.includes(c));
  const labels = columnsInOrder.map((c: string) => labelForColumn(c, group.name));
  isInserting.value = true;
  try {
    await insertTable(group.name, columnsInOrder, labels);
    emit('status', `Inserted table for {{${group.name}}}`);
    emit('inserted');
    // Persistent embedded flow: after a successful insert, reset for the
    // next table so users can insert a second one without navigating out.
    resetWizard();
  } catch (e) {
    emit('status', e instanceof Error ? `Insert failed: ${e.message}` : 'Insert failed');
  } finally {
    isInserting.value = false;
  }
}
</script>

<style scoped>
.wizard {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0 8px;
}

.wizard-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header {
  margin: 0;
}

.section-title {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 600;
  color: #201f1e;
}

.section-desc {
  margin: 0;
  font-size: 12px;
  color: #605e5c;
}

.wizard-step {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wizard-subheader {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 10px;
  background: #f3f2f1;
  border-left: 3px solid #0078d4;
  border-radius: 2px;
}

.wizard-subheader-eyebrow {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #605e5c;
}

.wizard-subheader-title {
  font-size: 13px;
  font-weight: 600;
  color: #201f1e;
}

.search-input {
  display: block;
  width: 100%;
  padding: 6px 10px;
  font-size: 12px;
  border: 1px solid #8a8886;
  border-radius: 2px;
  background: #fff;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: #0078d4;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #edebe9;
  border-radius: 2px;
  padding: 4px;
  background: #fff;
}

.option-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 2px;
}

.option-item:hover {
  background: #f3f2f1;
}

.option-item input {
  margin: 2px 0 0;
  flex-shrink: 0;
}

.option-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.option-title {
  font-size: 12px;
  font-weight: 500;
  color: #201f1e;
  word-break: break-word;
}

.option-sub {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 10px;
  color: #605e5c;
  word-break: break-all;
}

.column-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.column-toolbar-label {
  color: #605e5c;
}

.column-toolbar-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.link-sep {
  color: #a19f9d;
  font-size: 10px;
}

.link-btn {
  border: none;
  background: transparent;
  color: #0078d4;
  font: inherit;
  font-size: 11px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 2px;
}

.link-btn:hover {
  background: #edebe9;
  text-decoration: underline;
}

.empty-hint {
  margin: 4px 0 0;
  padding: 8px 10px;
  font-size: 12px;
  color: #605e5c;
  background: #f3f2f1;
  border-left: 3px solid #0078d4;
  border-radius: 2px;
}

.wizard-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #edebe9;
}

.btn {
  padding: 6px 14px;
  font-size: 12px;
  border-radius: 2px;
  cursor: pointer;
  border: 1px solid #8a8886;
  font-family: inherit;
}

.btn:disabled {
  cursor: not-allowed;
}

.btn-primary {
  background: #0078d4;
  color: #fff;
  border-color: #0078d4;
}

.btn-primary:hover:not(:disabled) {
  background: #106ebe;
  border-color: #106ebe;
}

.btn-primary:disabled {
  background: #f3f2f1;
  color: #a19f9d;
  border-color: #c8c6c4;
}
</style>
